/**
 * SYR-107 — the public Hypothesis lead queue.
 *
 * Every request to brandscience.ai/hypothesis lands here, whatever happened to
 * it. The rows that matter are the QUEUED_REVIEW ones: a real brand wanted
 * this and did not clear the gate, which is a conversation rather than a
 * rejection.
 *
 * One row per REQUEST, not per domain. Two people from the same company
 * running the tool independently is a buying signal, so a repeat shows up as
 * its own row with "cached" set — it cost nothing and it is still a lead.
 */

import React, { useEffect, useState } from 'react';
import {
    Button,
    Card,
    Empty,
    Popconfirm,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import {
    CheckCircleOutlined,
    CloudDownloadOutlined,
    DeleteOutlined,
    ExperimentOutlined,
    EyeOutlined,
    LinkOutlined,
    MailOutlined,
    ReloadOutlined,
    PlayCircleOutlined,
    StopOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    useGetHypothesisLeadStatsQuery,
    useGetHypothesisLeadsQuery,
    useGetHypothesisLeadResultLinkMutation,
    useReviewHypothesisLeadMutation,
    useDeleteHypothesisLeadMutation,
    useReformHypothesisSubjectMutation,
    useContinueHypothesisWithReviewsMutation,
} from '../../../services/nestjs-base-api';
import { IHypothesisLead, IHypothesisLeadPage } from '../../../services/nestjs-base-api/types';
import { ReviewSourcesModal } from '../hypothesis/ReviewSourcesModal';
import { ResultPreviewModal } from '../hypothesis/ResultPreviewModal';
import { LeadResearchPanel } from './LeadResearchPanel';

type IHypothesisLeadRow = IHypothesisLeadPage['items'][number];

const { Text, Paragraph } = Typography;

// Mirrors PAUSED_QUEUE_STATUS in admin-public-hypothesis.service.ts. A queue
// status, never a lead status: the table still says QUEUED_REVIEW.
const PAUSED_QUEUE_STATUS = 'PAUSED';
// Visitors type "www.example.com" more often than not. Rendered bare, the
// browser treats it as a path under /admin. Older rows still hold the raw
// value; newer ones are normalised on save.
const absoluteUrl = (url: string): string =>
    /^[a-z][a-z0-9+.-]*:\/\//i.test(url) ? url : `https://${url}`;

const queueStatusOf = (lead: { status: string; reasonCode: string | null }): string =>
    lead.status === 'QUEUED_REVIEW' && lead.reasonCode === 'tool_paused'
        ? PAUSED_QUEUE_STATUS
        : lead.status;

// Colours follow meaning, not novelty: green is a delivered result, amber is
// waiting on a human, red is a dead end.
const STATUS_COLOUR: Record<IHypothesisLead['status'] | typeof PAUSED_QUEUE_STATUS, string> = {
    // Queue-only: QUEUED_REVIEW with reason tool_paused. Gold rather than
    // amber because nothing is wrong with these — a real brand asked while
    // the tool was off, and they are the first to run when it comes back.
    PAUSED: 'gold',
    PENDING_PIN: 'default',
    VERIFIED: 'blue',
    RUNNING: 'processing',
    READY: 'green',
    QUEUED_REVIEW: 'orange',
    BLOCKED: 'red',
    INSUFFICIENT_DATA: 'purple',
    FAILED: 'red',
};

interface Props {
    jwt: string;
}

export const PublicHypothesisLeadsCard: React.FC<Props> = ({ jwt }) => {
    const intl = useIntl();
    const [status, setStatus] = useState<string>('QUEUED_REVIEW');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // A run takes minutes, and both of these used to fetch exactly once. You
    // pressed Re-run, the row left whatever filter you were on, and nothing
    // moved again until you thought to press Refresh — so a run in progress
    // was indistinguishable from a row that had been deleted. Worse, leads and
    // stats fetch independently, so the chips could read "Running: 1" over a
    // table saying "Nothing here yet".
    //
    // Polling only while something is actually in flight: an idle queue is
    // still a single fetch, and 10s is well inside a run's own tick.
    //
    // `watching` exists because polling cannot be gated on the fetched state
    // alone: the queue only learns a run started by refetching, and it only
    // refetches while it believes a run is in progress. Triggering one opens a
    // bounded window instead — long enough to observe the RUNNING row, after
    // which runningCount keeps the poll alive on its own and stops it when the
    // last run settles. Bounded so a trigger that never starts anything cannot
    // leave the page polling for ever.
    const [watching, setWatching] = useState(false);
    const [runningNow, setRunningNow] = useState(0);
    const pollMs = watching || runningNow > 0 ? 10000 : 0;
    const {
        data: leadPage,
        isFetching,
        refetch: refetchLeads,
    } = useGetHypothesisLeadsQuery(
        { jwtToken: jwt, status, page, limit: pageSize },
        { skip: !jwt, pollingInterval: pollMs },
    );
    const leads = leadPage?.items;
    const {
        data: stats,
        isFetching: statsFetching,
        refetch: refetchStats,
    } = useGetHypothesisLeadStatsQuery(
        { jwtToken: jwt },
        { skip: !jwt, pollingInterval: pollMs },
    );

    // Driven by the STATS rather than the visible rows: the run you just
    // started is usually not on the page any more, which is the whole problem.
    const runningCount = (stats as Record<string, number> | undefined)?.RUNNING ?? 0;
    // A job that has not reached an ending yet. Separate from lead status on
    // purpose: a re-form shows up ONLY here.
    const jobBusy = (l: IHypothesisLeadRow) =>
        !!l.jobStatus && !['READY', 'INSUFFICIENT_DATA', 'FAILED'].includes(l.jobStatus);
    const visibleRunning = (leads ?? []).some(
        (l) => l.status === 'RUNNING' || jobBusy(l),
    );
    useEffect(() => {
        setRunningNow(runningCount + (visibleRunning ? 1 : 0));
    }, [runningCount, visibleRunning]);
    useEffect(() => {
        if (!watching) return undefined;
        const timer = setTimeout(() => setWatching(false), 30000);
        return () => clearTimeout(timer);
    }, [watching]);

    // Both, deliberately: the counts along the top ARE the status filter, so
    // refreshing the rows without them would leave the two disagreeing about
    // what is in the queue.
    const refresh = () => {
        refetchLeads();
        refetchStats();
    };
    const [review, reviewState] = useReviewHypothesisLeadMutation();
    const [issueResultLink, resultLinkState] = useGetHypothesisLeadResultLinkMutation();
    const [deleteLead, deleteState] = useDeleteHypothesisLeadMutation();
    const [reform, reformState] = useReformHypothesisSubjectMutation();
    const [continueRun] = useContinueHypothesisWithReviewsMutation();

    // Formation only, against the research already stored. Costs one model
    // call instead of a whole crawl, and holds the evidence constant so a
    // changed read-out is the prompt's doing rather than a different crawl's.
    const reformLead = async (lead: IHypothesisLeadRow) => {
        if (!lead.subjectId) return;
        try {
            await reform({ jwtToken: jwt, subjectId: lead.subjectId }).unwrap();
            setWatching(true);
            refetchStats();
            message.success(
                intl.formatMessage({
                    id: 'publicHypothesis.admin.reformQueued',
                    defaultMessage: 'Re-forming from stored research — the runner picks it up within a minute.',
                }),
            );
        } catch {
            message.error(
                intl.formatMessage({
                    id: 'publicHypothesis.admin.reformFailed',
                    defaultMessage: 'Could not re-form. It may be mid-run — check the API log.',
                }),
            );
        }
    };

    // Hard delete, unlike Dismiss which keeps the row and its attribution.
    const removeLead = async (lead: IHypothesisLead) => {
        try {
            await deleteLead({ jwtToken: jwt, id: lead.id }).unwrap();
            message.success('Lead deleted');
        } catch {
            message.error('Could not delete lead');
        }
    };
    // The lead whose review pages are being edited, and the textarea contents.
    const [sourcesFor, setSourcesFor] = useState<IHypothesisLeadRow | null>(null);
    const [previewSubjectId, setPreviewSubjectId] = useState<number | null>(null);

    const changeStatus = (next: string) => {
        // Without this, switching from a 4-page filter to a 1-page one leaves
        // the table asking for page 4 and showing nothing.
        setStatus(next);
        setPage(1);
    };

    const act = async (
        lead: IHypothesisLead,
        action: 'approve' | 'dismiss' | 'to-crm',
        silent = false,
    ) => {
        try {
            await review({ jwtToken: jwt, id: lead.id, action, silent }).unwrap();
            // Watch it actually start, rather than leaving a row that looks
            // like it vanished from whatever filter you were on.
            setWatching(true);
            refetchStats();
            message.success(
                intl.formatMessage(
                    { id: 'publicHypothesis.admin.actionDone', defaultMessage: 'Done: {email}' },
                    { email: lead.email },
                ),
            );
        } catch {
            message.error(
                intl.formatMessage({
                    id: 'publicHypothesis.admin.actionFailed',
                    defaultMessage: 'That did not work. Check the API log.',
                }),
            );
        }
    };

    // The magic link the customer was emailed, reissued and put on the
    // clipboard. The original is not stored anywhere and does not need to be:
    // the token points at the lead rather than carrying a session, so a fresh
    // one opens the same read-out. The clock restarts, which is why the
    // confirmation says how long this one lasts.
    const copyResultLink = async (lead: IHypothesisLead) => {
        try {
            const { url, expiresInDays } = await issueResultLink({
                jwtToken: jwt,
                id: lead.id,
            }).unwrap();
            // Clipboard access needs a secure context. On plain http over a LAN
            // address it is simply absent, so the link is shown to be copied by
            // hand rather than silently lost.
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
                message.success(
                    intl.formatMessage(
                        {
                            id: 'publicHypothesis.admin.linkCopied',
                            defaultMessage: 'Link copied. It works for {days} days.',
                        },
                        { days: expiresInDays },
                    ),
                );
            } else {
                message.info(url, 10);
            }
        } catch (error) {
            const detail = (error as { data?: { message?: string } })?.data?.message;
            message.error(
                detail ||
                    intl.formatMessage({
                        id: 'publicHypothesis.admin.linkFailed',
                        defaultMessage: 'No link was issued. Check the API log.',
                    }),
            );
        }
    };

    const columns = [
        {
            title: intl.formatMessage({ id: 'publicHypothesis.admin.brand', defaultMessage: 'Brand' }),
            key: 'brand',
            width: 185,
            render: (_: unknown, lead: IHypothesisLeadRow) => (
                <Space direction="vertical" size={0}>
                    <Space size={6}>
                        <Text strong ellipsis style={{ maxWidth: 108 }}>
                            {lead.derivedBrandName || lead.targetDomain || '—'}
                        </Text>
                        {/* Ours, not a prospect's. Without this a run we did to
                            check the tool reads as demand in the queue. */}
                        {lead.isInternal ? (
                            <Tag color="default" style={{ fontSize: 11, lineHeight: '16px' }}>
                                <FormattedMessage
                                    id="publicHypothesis.admin.internal"
                                    defaultMessage="Internal"
                                />
                            </Tag>
                        ) : null}
                    </Space>
                    <a href={absoluteUrl(lead.submittedUrl)} target="_blank" rel="noreferrer">
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {lead.targetDomain}
                        </Text>
                    </a>
                </Space>
            ),
        },
        {
            title: intl.formatMessage({ id: 'publicHypothesis.admin.contact', defaultMessage: 'Contact' }),
            key: 'contact',
            width: 185,
            render: (_: unknown, lead: IHypothesisLeadRow) => (
                <Space direction="vertical" size={0}>
                    <Tooltip title={lead.email}>
                        <Text ellipsis style={{ maxWidth: 161 }}>
                            {lead.email}
                        </Text>
                    </Tooltip>
                    {lead.unsubscribedAt ? (
                        <Tag color="red">
                            <FormattedMessage
                                id="publicHypothesis.admin.unsubscribed"
                                defaultMessage="Unsubscribed"
                            />
                        </Tag>
                    ) : null}
                </Space>
            ),
        },
        {
            title: intl.formatMessage({ id: 'publicHypothesis.admin.status', defaultMessage: 'Status' }),
            key: 'status',
            width: 130,
            render: (_: unknown, lead: IHypothesisLeadRow) => (
                <Space direction="vertical" size={2}>
                    {queueStatusOf(lead) === PAUSED_QUEUE_STATUS ? (
                        <Tag color={STATUS_COLOUR.PAUSED}>{PAUSED_QUEUE_STATUS}</Tag>
                    ) : (
                        <Tag color={STATUS_COLOUR[lead.status]}>{lead.status}</Tag>
                    )}
                    {/* Which stage it is on, so a run that is working looks
                        different from a run that is stuck. The runner already
                        records this; the queue simply never showed it. */}
                    {jobBusy(lead) ? (
                        <Tag color="processing" style={{ fontSize: 11 }}>
                            {(lead.progressStage || lead.jobStatus || '')
                                .toLowerCase()
                                .replace(/_/g, ' ')}
                        </Tag>
                    ) : null}
                    {/* reasonCode is a CATEGORY: 'run_failed' covers a
                        timeout, a bad key and a rejected element pick alike.
                        The job's own message is the only thing that separates
                        them, and without it here the answer to "why did this
                        fail" was a CloudWatch dig. attemptCount rides along
                        because last_error holds only the LATEST message —
                        3 of 3 means it repeated and will repeat again. */}
                    {lead.reasonCode && queueStatusOf(lead) !== PAUSED_QUEUE_STATUS ? (
                        lead.lastError ? (
                            <Tooltip
                                title={
                                    <span style={{ whiteSpace: 'pre-wrap' }}>
                                        {lead.lastError}
                                        {'\n\n'}
                                        {intl.formatMessage(
                                            {
                                                id: 'publicHypothesis.admin.failureMeta',
                                                defaultMessage:
                                                    'Attempt {attempts} of 3{stage, select, none {} other { · stopped at {stage}}}',
                                            },
                                            {
                                                attempts: lead.attemptCount,
                                                stage: lead.progressStage ?? 'none',
                                            },
                                        )}
                                    </span>
                                }
                                overlayStyle={{ maxWidth: 460 }}
                            >
                                <Text
                                    type="danger"
                                    style={{
                                        fontSize: 12,
                                        cursor: 'help',
                                        textDecoration: 'underline dotted',
                                    }}
                                >
                                    {lead.reasonCode}
                                </Text>
                            </Tooltip>
                        ) : (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {lead.reasonCode}
                            </Text>
                        )
                    ) : null}
                    {lead.servedCached ? (
                        <Tooltip
                            title={intl.formatMessage({
                                id: 'publicHypothesis.admin.cachedHint',
                                defaultMessage:
                                    'Served an existing hypothesis. This lead cost nothing to fulfil.',
                            })}
                        >
                            <Tag icon={<CloudDownloadOutlined />} color="cyan">
                                <FormattedMessage
                                    id="publicHypothesis.admin.cached"
                                    defaultMessage="Cached"
                                />
                            </Tag>
                        </Tooltip>
                    ) : null}
                </Space>
            ),
        },
        {
            title: intl.formatMessage({ id: 'publicHypothesis.admin.delivered', defaultMessage: 'Result sent' }),
            key: 'sent',
            width: 155,
            render: (_: unknown, lead: IHypothesisLeadRow) =>
                lead.resultEmailSentAt ? (
                    <Space size={4}>
                        <CheckCircleOutlined style={{ color: '#16a34a' }} />
                        <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                            {new Date(lead.resultEmailSentAt).toLocaleString()}
                        </Text>
                    </Space>
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            title: intl.formatMessage({ id: 'publicHypothesis.admin.requested', defaultMessage: 'Requested' }),
            key: 'created',
            width: 165,
            render: (_: unknown, lead: IHypothesisLeadRow) => (
                <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {new Date(lead.createdAt).toLocaleString()}
                </Text>
            ),
        },
        {
            title: intl.formatMessage({ id: 'publicHypothesis.admin.aiCost', defaultMessage: 'AI cost' }),
            key: 'aiCost',
            width: 96,
            align: 'right' as const,
            onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' as const } }),
            render: (_: unknown, lead: IHypothesisLeadRow) =>
                lead.aiTokens ? (
                    <Tooltip
                        title={intl.formatMessage(
                            {
                                id: 'publicHypothesis.admin.aiCostHint',
                                defaultMessage:
                                    '{tokens} tokens across {calls} model calls',
                            },
                            { tokens: lead.aiTokens.toLocaleString(), calls: lead.aiCalls },
                        )}
                    >
                        <Text
                            style={{
                                fontVariantNumeric: 'tabular-nums',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            ${lead.aiCostUsd.toFixed(3)}
                        </Text>
                    </Tooltip>
                ) : (
                    // Not zero: a cached or gated lead never ran, and showing
                    // $0.000 would read as a free run rather than no run.
                    <Text type="secondary">—</Text>
                ),
        },
        {
            title: intl.formatMessage({ id: 'publicHypothesis.admin.actions', defaultMessage: 'Actions' }),
            key: 'actions',
            width: 200,
            fixed: 'right' as const,
            render: (_: unknown, lead: IHypothesisLeadRow) => {
                const canRun =
                    lead.status === 'QUEUED_REVIEW' ||
                    lead.status === 'BLOCKED' ||
                    lead.status === 'FAILED';
                return (
                    <Space size={4} wrap>
                        {canRun ? (
                            <Popconfirm
                                title={intl.formatMessage({
                                    id: 'publicHypothesis.admin.approveConfirm',
                                    defaultMessage: 'Run The Hypothesis for this brand? This spends AI credit.',
                                })}
                                onConfirm={() => act(lead, 'approve')}
                            >
                                <Button
                                    size="small"
                                    type="primary"
                                    icon={<PlayCircleOutlined style={{ marginRight: 4 }} />}
                                    loading={reviewState.isLoading}
                                >
                                    <FormattedMessage
                                        id="publicHypothesis.admin.approve"
                                        defaultMessage="Run it"
                                    />
                                </Button>
                            </Popconfirm>
                        ) : null}
                        {/* A finished read-out is the product. Being able to
                            see exactly what was sent, without digging a magic
                            link out of an inbox, is the difference between
                            checking it and hoping. */}
                        {lead.status === 'READY' && lead.subjectId ? (
                            <Button
                                size="small"
                                icon={<EyeOutlined style={{ marginRight: 4 }} />}
                                onClick={() => setPreviewSubjectId(lead.subjectId)}
                            >
                                <FormattedMessage
                                    id="publicHypothesis.admin.viewResult"
                                    defaultMessage="View result"
                                />
                            </Button>
                        ) : null}
                        {/* Gated on having a SUBJECT, not on canRun: review
                            pages attach to the subject, so the question is
                            whether one exists — and a READY lead is exactly
                            when you would want to add a source and try again. */}
                        {lead.subjectId ? (
                            <Tooltip
                                title={intl.formatMessage({
                                    id: 'publicHypothesis.admin.reviewSources',
                                    defaultMessage: 'Review pages',
                                })}
                            >
                                <Button
                                    size="small"
                                    icon={<LinkOutlined />}
                                    onClick={() => setSourcesFor(lead)}
                                />
                            </Tooltip>
                        ) : null}
                        {/* Formation only, no crawl. The prompt-iteration
                            loop: same evidence in, so a different read-out is
                            attributable to the prompt. Needs stored research,
                            hence the researchCompletedAt guard rather than a
                            status check — INSUFFICIENT_DATA runs crawled fine
                            and are exactly the ones worth re-forming once the
                            gate or the prompt moves. Quiet by construction:
                            re-form never clears the delivery stamp. */}
                        {lead.subjectId && lead.researchCompletedAt ? (
                            <Popconfirm
                                title={intl.formatMessage({
                                    id: 'publicHypothesis.admin.reformConfirm',
                                    defaultMessage: 'Re-form the hypothesis from the research already collected. No crawl, no email, and the previous hypothesis is replaced.',
                                })}
                                onConfirm={() => reformLead(lead)}
                                okText={intl.formatMessage({ id: 'publicHypothesis.admin.reformOk', defaultMessage: 'Re-form' })}
                            >
                                <Tooltip
                                    title={intl.formatMessage({
                                        id: 'publicHypothesis.admin.reform',
                                        defaultMessage: 'Re-form only, no crawl',
                                    })}
                                >
                                    <Button
                                        size="small"
                                        icon={<ExperimentOutlined />}
                                        loading={reformState.isLoading}
                                    />
                                </Tooltip>
                            </Popconfirm>
                        ) : null}
                        {/* Re-run after fixing OUR input — a pasted review
                            page, a corrected source — where the customer has
                            already had their email. Leaves the delivery stamp
                            alone, so the read-out behind the link they hold is
                            replaced without a second "here it is again" email.
                            Hidden when nothing was sent, because then there is
                            no email to avoid and Approve is the same thing. */}
                        {lead.status === 'READY' && lead.resultEmailSentAt ? (
                            <Popconfirm
                                title={intl.formatMessage({
                                    id: 'publicHypothesis.admin.rerunQuietConfirm',
                                    defaultMessage: 'Re-run and replace the read-out behind their existing link. No email is sent.',
                                })}
                                onConfirm={() => act(lead, 'approve', true)}
                                okText={intl.formatMessage({ id: 'publicHypothesis.admin.rerunQuietOk', defaultMessage: 'Re-run' })}
                            >
                                <Tooltip
                                    title={intl.formatMessage({
                                        id: 'publicHypothesis.admin.rerunQuiet',
                                        defaultMessage: 'Re-run, no email',
                                    })}
                                >
                                    <Button size="small" icon={<ReloadOutlined />} />
                                </Tooltip>
                            </Popconfirm>
                        ) : null}
                        {/* READY only. A link to a read-out that does not
                            exist yet lands on an error page, and a link we
                            hand out ourselves is the one that must not be
                            broken. */}
                        {lead.status === 'READY' ? (
                            <Tooltip
                                title={intl.formatMessage({
                                    id: 'publicHypothesis.admin.copyResultLink',
                                    defaultMessage: 'Copy result link',
                                })}
                            >
                                <Button
                                    size="small"
                                    icon={<MailOutlined />}
                                    loading={resultLinkState.isLoading}
                                    onClick={() => copyResultLink(lead)}
                                />
                            </Tooltip>
                        ) : null}
                        {!lead.crmCompanyId ? (
                            <Tooltip
                                title={intl.formatMessage({
                                    id: 'publicHypothesis.admin.toCrm',
                                    defaultMessage: 'Add to CRM',
                                })}
                            >
                                <Button
                                    size="small"
                                    icon={<TeamOutlined />}
                                    onClick={() => act(lead, 'to-crm')}
                                />
                            </Tooltip>
                        ) : (
                            <Tag color="blue">
                                <FormattedMessage
                                    id="publicHypothesis.admin.inCrm"
                                    defaultMessage="On the CRM"
                                />
                            </Tag>
                        )}
                        {lead.status !== 'BLOCKED' ? (
                            <Tooltip
                                title={intl.formatMessage({
                                    id: 'publicHypothesis.admin.dismiss',
                                    defaultMessage: 'Dismiss',
                                })}
                            >
                                <Button
                                    size="small"
                                    danger
                                    icon={<StopOutlined />}
                                    onClick={() => act(lead, 'dismiss')}
                                />
                            </Tooltip>
                        ) : null}
                        {/* Hard delete. Dismiss keeps the row and its
                            attribution; this is for entries that should never
                            have counted, like our own test runs. */}
                        <Popconfirm
                            title={intl.formatMessage({
                                id: 'publicHypothesis.admin.deleteConfirm',
                                defaultMessage: 'Delete this lead permanently?',
                            })}
                            description={intl.formatMessage({
                                id: 'publicHypothesis.admin.deleteConfirmBody',
                                defaultMessage:
                                    'Removes the row and its attribution. Use Dismiss to keep it in the funnel numbers.',
                            })}
                            okText={intl.formatMessage({
                                id: 'publicHypothesis.admin.deleteOk',
                                defaultMessage: 'Delete',
                            })}
                            okButtonProps={{ danger: true }}
                            onConfirm={() => removeLead(lead)}
                        >
                            <Tooltip
                                title={intl.formatMessage({
                                    id: 'publicHypothesis.admin.delete',
                                    defaultMessage: 'Delete',
                                })}
                            >
                                <Button
                                    size="small"
                                    danger
                                    loading={deleteState.isLoading}
                                    icon={<DeleteOutlined />}
                                />
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    // The counts ARE the filter. There used to be a dropdown as well, which
    // read "Queued for review" beside a tag reading "QUEUED_REVIEW" — two
    // controls, two vocabularies, for one job.
    const STATUS_LABEL: Record<string, string> = {
        PAUSED: intl.formatMessage({
            id: 'publicHypothesis.admin.statusPaused',
            defaultMessage: 'Paused, ready to run',
        }),
        QUEUED_REVIEW: 'Queued for review',
        READY: 'Ready',
        RUNNING: 'Running',
        PENDING_PIN: 'Awaiting code',
        INSUFFICIENT_DATA: 'Not enough data',
        FAILED: 'Failed',
        BLOCKED: 'Blocked',
        VERIFIED: 'Verified',
    };
    // Fixed order, so a chip does not move under the cursor as counts change.
    const STATUS_ORDER = [
        'PAUSED',
        'QUEUED_REVIEW',
        'PENDING_PIN',
        'RUNNING',
        'READY',
        'INSUFFICIENT_DATA',
        'FAILED',
        'BLOCKED',
    ];
    const statusCounts = Object.fromEntries(
        Object.entries(stats ?? {}).filter(([key]) => !key.startsWith('_')),
    ) as Record<string, number>;
    const totalLeads = Object.values(statusCounts).reduce((sum, n) => sum + n, 0);

    return (
        <Card
            title={
                <span>
                    <TeamOutlined style={{ marginRight: 8 }} />
                    <FormattedMessage
                        id="publicHypothesis.admin.title"
                        defaultMessage="Lead generator queue"
                    />
                </span>
            }
            style={{ marginTop: 24 }}
            extra={
                <Button
                    size="small"
                    icon={<ReloadOutlined style={{ marginRight: 4 }} />}
                    onClick={refresh}
                    loading={isFetching || statsFetching}
                >
                    <FormattedMessage
                        id="publicHypothesis.admin.refresh"
                        defaultMessage="Refresh"
                    />
                </Button>
            }
        >
            <Paragraph type="secondary">
                <FormattedMessage
                    id="publicHypothesis.admin.intro"
                    defaultMessage="Requests from brandscience.ai/hypothesis. Anything queued did not clear the gate: a personal email address, a domain that does not match the site, or a brand already on the platform. Paused ones cleared the gate but arrived while the tool was switched off; Run it starts them. Running one spends AI credit."
                />
            </Paragraph>

            {stats ? (
                <Space wrap style={{ marginBottom: 16 }}>
                    <Tag.CheckableTag
                        checked={status === 'all'}
                        onChange={() => changeStatus('all')}
                    >
                        <FormattedMessage
                            id="publicHypothesis.admin.filterAll"
                            defaultMessage="All"
                        />{' '}
                        {totalLeads}
                    </Tag.CheckableTag>
                    {STATUS_ORDER.filter((key) => statusCounts[key]).map((key) => {
                        const active = status === key;
                        const colour = STATUS_COLOUR[key as keyof typeof STATUS_COLOUR];
                        return (
                            <Tag
                                key={key}
                                color={active ? colour : undefined}
                                onClick={() => changeStatus(active ? 'all' : key)}
                                style={{
                                    cursor: 'pointer',
                                    // Unselected chips keep the status colour as
                                    // an outline, so the palette still reads at a
                                    // glance without four filled tags competing
                                    // with the one you actually chose.
                                    borderColor: active ? undefined : colour,
                                    color: active ? undefined : colour,
                                    fontWeight: active ? 600 : 400,
                                }}
                            >
                                {STATUS_LABEL[key] ?? key}: {statusCounts[key]}
                            </Tag>
                        );
                    })}
                    {/* What the tool has cost, next to what it has produced.
                        These two belong on the same line: run volume on its own
                        looks good and means nothing. */}
                    {stats._totalAiTokens ? (
                        <Tag color="purple">
                            <FormattedMessage
                                id="publicHypothesis.admin.totalSpend"
                                defaultMessage="AI spend: {tokens} tokens across {leads} runs"
                                values={{
                                    tokens: stats._totalAiTokens.toLocaleString(),
                                    leads: stats._leadsWithSpend ?? 0,
                                }}
                            />
                        </Tag>
                    ) : null}
                </Space>
            ) : null}

            <Table
                dataSource={leads ?? []}
                columns={columns}
                rowKey="id"
                loading={isFetching}
                size="small"
                // Without a declared width the browser squeezes every column
                // to fit and breaks text mid-word — "Cygne/tt", "$0.19/9" —
                // which for a cost figure reads as a different number. Scroll
                // rather than compress.
                scroll={{ x: 1110 }}
                // The research is one click from the row it belongs to, and
                // fetched only when opened — a queue page holds 25 rows and
                // most of them are never inspected.
                expandable={{
                    expandedRowRender: (lead: IHypothesisLeadRow) => (
                        <LeadResearchPanel jwt={jwt} subjectId={lead.subjectId} />
                    ),
                    rowExpandable: (lead: IHypothesisLeadRow) => !!lead.subjectId,
                }}
                pagination={{
                    current: leadPage?.page ?? 1,
                    pageSize: leadPage?.limit ?? pageSize,
                    total: leadPage?.total ?? 0,
                    showSizeChanger: true,
                    pageSizeOptions: ['25', '50', '100'],
                    showTotal: (total, range) =>
                        intl.formatMessage(
                            {
                                id: 'publicHypothesis.admin.showTotal',
                                defaultMessage: '{from}-{to} of {total} leads',
                            },
                            { from: range[0], to: range[1], total },
                        ),
                    onChange: (nextPage, nextSize) => {
                        setPage(nextPage);
                        if (nextSize) setPageSize(nextSize);
                    },
                }}
                locale={{
                    emptyText: (
                        <Empty
                            description={intl.formatMessage({
                                id: 'publicHypothesis.admin.empty',
                                defaultMessage: 'Nothing here yet.',
                            })}
                        />
                    ),
                }}
            />

            <ResultPreviewModal
                jwt={jwt}
                subjectId={previewSubjectId}
                onClose={() => setPreviewSubjectId(null)}
            />

            <ReviewSourcesModal
                jwt={jwt}
                subjectId={sourcesFor?.subjectId ?? null}
                brandName={sourcesFor?.derivedBrandName || sourcesFor?.targetDomain}
                onClose={() => setSourcesFor(null)}
                // Saving alone changes nothing until the research runs again,
                // so the two are one action. Approve is the queue's re-run.
                // Which path depends on WHY the run needs sources. A job
                // parked at AWAITING_REVIEWS already holds its crawl, so
                // continuing costs one synthesis; anything else never got that
                // far and has to be run properly.
                onSaved={async () => {
                    if (!sourcesFor) return;
                    if (sourcesFor.reasonCode === 'awaiting_reviews' && sourcesFor.subjectId) {
                        const result = await continueRun({
                            jwtToken: jwt,
                            subjectId: sourcesFor.subjectId,
                        }).unwrap();
                        if (result.resumed) {
                            message.success(
                                intl.formatMessage(
                                    {
                                        id: 'publicHypothesis.admin.continued',
                                        defaultMessage:
                                            'Continuing with {count} review source(s) — no re-crawl needed.',
                                    },
                                    { count: result.sourcesFound ?? 0 },
                                ),
                            );
                            setWatching(true);
                            refetchStats();
                        } else {
                            message.warning(result.reason || 'Could not continue');
                        }
                        return;
                    }
                    await act(sourcesFor, 'approve');
                }}
            />
        </Card>
    );
};
